import { Alert, Checkbox, CircularProgress, FormControlLabel, FormGroup } from "@mui/material";
import React from "react";
import { Field, Form } from "react-final-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { Redirect } from "react-router-dom";
import { PageLoading } from "../../../common/components/Loading";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { selfActions } from "../../../common/reducers/selfSlice";
import { Api } from "../../../common/service/Api";
import { UserDetailsDto } from "../../../common/service/server/dto/UserDto";
import { AuthPageWrapper } from "../../components/layout/AuthPageWrapper";
import { Error as RequestsError } from "../../copy/requests";
import { Routes } from "../../root/routes";
import { Validation } from "../../util";
import { Button, TextInput } from "@truevault/aquilo";

export const SetPassword = () => {
  const dispatch = useDispatch();
  const params = useParams() as any;

  const { result: self, request: selfRequest } = useDataRequest({
    queryKey: ["self", params.resetToken],
    api: () => Api.user.getSelfByPasswordUpdateId(params.resetToken),
    messages: {
      defaultError: RequestsError.GetWithResetToken,
    },
  });

  const { fetch: setPassword, request: setPasswordRequest } = useActionRequest({
    api: (values) =>
      Api.user.updateUserPassword(self?.email, (values as any).password, params.resetToken),
    messages: {
      defaultError: RequestsError.SetPassword,
    },
    onSuccess: (user) => {
      dispatch(selfActions.set(user as UserDetailsDto));
      window.location.replace(Routes.root);
    },
  });

  if (NetworkRequest.areFinished(selfRequest) && !self) {
    // redirect to login
    return <Redirect to={Routes.auth.login} />;
  }

  if (!NetworkRequest.areFinished(selfRequest)) {
    return <PageLoading />;
  }

  return (
    <AuthPageWrapper image="/assets/images/reset-password.svg">
      <p>Reset your password</p>

      <SetPasswordFormWrapper
        setPassword={setPassword}
        setPasswordRequest={setPasswordRequest}
        showTos={false}
      />
    </AuthPageWrapper>
  );
};

export const SetPasswordFormWrapper = ({ setPassword, setPasswordRequest, showTos }) => {
  return (
    <Form
      onSubmit={setPassword}
      validate={(values) => setPasswordValidation(values, showTos)}
      render={(props) => (
        <SetPasswordForm {...props} setPasswordRequest={setPasswordRequest} showTos={showTos} />
      )}
    />
  );
};

const setPasswordValidation = (values, showTos) => {
  const errors = {} as any;

  Validation.requiredPassword(values, "password", errors);
  if (showTos && !values.tos) {
    errors.tos = "You must accept the Terms of Use.";
  }

  return errors;
};

const SetPasswordForm = ({ handleSubmit, invalid, setPasswordRequest, showTos }) => {
  return (
    <form onSubmit={handleSubmit}>
      <div className="tw-boundary">
        <Field name="password">
          {(props) => (
            <>
              <TextInput
                autoFocus
                type="password"
                field="password"
                id="password"
                onChange={props.input.onChange}
                label="Password"
                labelTextColor="text-neutral-700"
                required
              />
              {props.meta.invalid && props.meta.touched && (
                <div className="text-red-800 text-[12px]">{props.meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>

      {Boolean(showTos) && (
        <Field name="tos" type="checkbox">
          {(props) => (
            <FormGroup className="mb-md">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(props.input.checked)}
                    onChange={(_, checked) => props.input.onChange(checked)}
                    name={props.input.name}
                    color="primary"
                  />
                }
                label={
                  <>
                    I agree to{" "}
                    <a
                      href="https://www.truevault.com/legal/truevault-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </a>
                  </>
                }
              />
              {props.meta.invalid && (props.meta.touched || props.meta.submitFailed) && (
                <Alert severity="error">{props.meta.error}</Alert>
              )}
            </FormGroup>
          )}
        </Field>
      )}

      <div className="tw-boundary">
        <Button onClick={handleSubmit} color="primary" width="full" disabled={invalid}>
          {setPasswordRequest.running ? (
            <CircularProgress size={20} color="secondary" />
          ) : (
            "Set Password"
          )}
        </Button>
      </div>
    </form>
  );
};
